import { Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useSelector } from "react-redux";
import { selectBuilderDetailedView } from "../../../../store/selectors/fieldsStateSelectors.ts";
import type { StatementAxisState } from "../../../../store/types/fieldsState.types.ts";
import { DEFAULT_AREA_FIELD_ITEM_HEIGHT } from "../../../viewer/common/fields/area-fields/AreaFieldsHelper.ts";
import { getDateGroupingLabel } from "../../../viewer/common/fields/field-captions/captionsHelper.ts";
import CardConditionsButton from "../common/CardConditionsButton.tsx";
import CardDeleteButton from "../common/CardDeleteButton.tsx";
import CardOptionsButton from "../common/CardOptionsButton.tsx";
import { isDateAxis } from "../utils/statementColumns.ts";
import { getFieldConditionCount } from "../utils/statementOptions.ts";
import AxisConditionsPanel from "./AxisConditionsPanel.tsx";

interface Props {
  axis: StatementAxisState;
  optionsOpen: boolean;
  readonly: boolean;
  onShowOptions: (guid: string, anchorEl: HTMLElement) => void;
  onRemove: (guid: string) => void;
}

const AxisCard = ({ axis, optionsOpen, readonly, onShowOptions, onRemove }: Props) => {
  const [hovered, setHovered] = useState(false);
  const [conditionsAnchorEl, setConditionsAnchorEl] = useState<HTMLElement>();
  const detailedView = useSelector(selectBuilderDetailedView);

  const { field } = axis;
  const guid = field.config.guid;
  const isDate = isDateAxis(axis);
  const conditionCount = isDate ? 0 : getFieldConditionCount(field);
  const showHoverActions = hovered && !readonly;
  const showConditionsButton = !isDate && (showHoverActions || conditionCount > 0 || conditionsAnchorEl !== undefined);

  return (
    <Stack
      sx={(theme) => ({
        border: `1px solid ${optionsOpen ? theme.palette.primary.main : theme.palette.divider}`,
        borderRadius: "2px",
        bgcolor: "background.paper",
      })}
    >
      <Stack
        direction="row"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{ minHeight: DEFAULT_AREA_FIELD_ITEM_HEIGHT, alignItems: "center" }}
      >
        <Stack direction="row" sx={{ flex: 1, minWidth: 0, alignItems: "center", gap: 0.75, px: 1.25 }}>
          <Typography variant="caption" noWrap sx={{ flex: 1, minWidth: 0 }}>
            {field.meta.caption}
          </Typography>
          {isDate && !showHoverActions && (
            <Typography variant="caption" noWrap color="primary">
              {getDateGroupingLabel(field.config.grouping)}
            </Typography>
          )}
        </Stack>
        {showHoverActions && <CardDeleteButton onDelete={() => onRemove(guid)} />}
        {showConditionsButton && (
          <CardConditionsButton
            conditionCount={conditionCount}
            detailedView={detailedView}
            onClick={setConditionsAnchorEl}
          />
        )}
        <CardOptionsButton title="Axis Options" onClick={(anchorEl) => onShowOptions(guid, anchorEl)} />
      </Stack>
      {!isDate && (conditionsAnchorEl !== undefined || (detailedView && conditionCount > 0)) && (
        <AxisConditionsPanel
          axis={axis}
          readonly={readonly}
          popupAnchorEl={conditionsAnchorEl}
          onClosePopup={() => setConditionsAnchorEl(undefined)}
        />
      )}
    </Stack>
  );
};

export default AxisCard;
